/* Atlas v3 — Calendar: upcoming earnings, in the v3 design language.

   Data: REUSED, not rebuilt. The live source is `earnings_calendar`
   (cron-populated Sundays for the week ahead), exposed by the existing
   useEarnings() hook → /api/atlas-data?resource=earnings (future-only,
   sorted asc). Company names come from the universe (useAtlasStocks).
   The old v2 source (/api/calendar over the curated `calendar_events`
   table) was stale editorial and had no live caller; it was DELETED
   2026-08-11. `earnings_calendar` is the only calendar source — the
   `calendar_events` / `calendar_event_stats` / `calendar_weekly_headers`
   tables still exist in Supabase but nothing reads them.

   v3 rules: true-black base, hairline-divided day groups (no bordered
   cards / icon tiles), mono labels + eyebrows, Display headline, amber
   as line/label only. Earnings have no direction → neutral text, amber
   day-relative tag. Tap a row → openTicker (the shared TickerSheet).

   Brings its own shell (TickerStrip + BottomNav) like the other v3
   screens. No starfield (Overview-only). */
import { useMemo } from 'react'
import { TickerCtx } from './lib'
import { TickerStrip, BottomNav, SearchPill } from './shell'
import { StockLogo } from '@/v2/components/StockLogo'
import { StockDetailProvider, useStockDetail } from '@/v2/lib/StockDetailProvider'
import { useEarnings } from '@/v2/hooks/useSupabaseData'
import { useAtlasStocks } from '@/v2/hooks/useAtlasStocks'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Sans = { fontFamily: 'var(--studio-font-sans)' }
const Display = { fontFamily: 'var(--studio-font-display)', fontStyle: 'italic' }

// Relative day tag — TODAY / TMRW / weekday within a week / "JUN 23" beyond.
function relTag(dateStr) {
  if (!dateStr) return ''
  try {
    const today = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }))
    today.setHours(0, 0, 0, 0)
    const d = new Date(dateStr + 'T00:00:00')
    const days = Math.round((d - today) / 86400000)
    if (days <= 0) return 'TODAY'
    if (days === 1) return 'TMRW'
    if (days < 7) return d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase()
  } catch { return '' }
}

// Full day header — "MONDAY · JUN 23".
function dayHeader(dateStr) {
  try {
    const d = new Date(dateStr + 'T00:00:00')
    const wd = d.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase()
    const md = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase()
    return `${wd} · ${md}`
  } catch { return dateStr }
}

const SESSION_LABEL = { bmo: 'BEFORE OPEN', amc: 'AFTER CLOSE' }

function EarningsRow({ ev, name, idx, onTicker }) {
  return (
    <button
      onClick={() => onTicker(ev.ticker)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
        padding: '13px 22px', cursor: 'pointer', border: 'none', background: 'transparent',
        borderTop: idx ? '1px solid hsl(var(--studio-border) / 0.35)' : 'none',
      }}>
      <StockLogo ticker={ev.ticker} size={30} rounded={6} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...Mono, fontSize: 13.5, color: 'hsl(var(--studio-text))' }}>{ev.ticker}</div>
        <div style={{ ...Sans, fontSize: 11, color: 'hsl(var(--studio-text-subtle))', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180, marginTop: 2 }}>{name || ev.quarter || ''}</div>
      </div>
      {ev.time && SESSION_LABEL[ev.time] && (
        <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', flexShrink: 0 }}>{SESSION_LABEL[ev.time]}</span>
      )}
      <span style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.06em', color: 'hsl(var(--studio-accent-soft))', flexShrink: 0, minWidth: 34, textAlign: 'right' }}>{relTag(ev.date)}</span>
    </button>
  )
}

function CalendarBody() {
  const { openTicker } = useStockDetail()
  const { earnings, loading } = useEarnings()
  const { stocks } = useAtlasStocks({ includeInactive: true })

  const nameByTicker = useMemo(() => {
    const m = {}
    for (const s of stocks || []) m[s.ticker] = s.name
    return m
  }, [stocks])

  // Group the (already future-only, asc-sorted) earnings by date.
  const groups = useMemo(() => {
    const by = new Map()
    for (const e of (earnings || [])) {
      if (!e.date) continue
      if (!by.has(e.date)) by.set(e.date, [])
      by.get(e.date).push(e)
    }
    return [...by.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1))
  }, [earnings])

  return (
    <TickerCtx.Provider value={openTicker}>
      <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column',
        background: 'radial-gradient(140% 75% at 50% -10%, hsl(var(--studio-background)) 0%, hsl(var(--studio-overlay)) 100%)' }}>
        <div style={{ flexShrink: 0, padding: '20px 20px 4px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.18em', color: 'hsl(var(--studio-accent))', marginBottom: 5 }}>WHAT&rsquo;S AHEAD</div>
            <div style={{ ...Display, fontSize: 32, color: 'hsl(var(--studio-text))', lineHeight: 1 }}>Calendar</div>
            <div style={{ ...Sans, fontSize: 12, color: 'hsl(var(--studio-text-muted))', marginTop: 6 }}>Upcoming earnings across the Atlas universe</div>
          </div>
          <SearchPill />
        </div>

        <div className="atlas-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', marginTop: 16 }}>
          {loading && (
            <div style={{ ...Sans, fontSize: 12, color: 'hsl(var(--studio-text-subtle))', padding: '18px 22px' }}>Loading the calendar…</div>
          )}

          {!loading && groups.length === 0 && (
            <div style={{ padding: '40px 24px', textAlign: 'center' }}>
              <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.18em', color: 'hsl(var(--studio-text-subtle))', marginBottom: 10 }}>NO EARNINGS SCHEDULED</div>
              <div style={{ ...Sans, fontSize: 13, lineHeight: 1.55, color: 'hsl(var(--studio-text-muted))', maxWidth: 320, margin: '0 auto' }}>
                No upcoming earnings on the calendar right now. The earnings calendar populates Sunday evening for the week ahead.
              </div>
            </div>
          )}

          {!loading && groups.map(([date, evs]) => (
            <div key={date} style={{ marginBottom: 6 }}>
              <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))', padding: '14px 22px 8px' }}>{dayHeader(date)}</div>
              <div style={{ borderTop: '1px solid hsl(var(--studio-border) / 0.35)' }}>
                {evs.map((ev, i) => <EarningsRow key={ev.ticker + ev.date} ev={ev} name={nameByTicker[ev.ticker]} idx={i} onTicker={openTicker} />)}
              </div>
            </div>
          ))}

          <div style={{ padding: '20px', ...Mono, fontSize: 8.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))', textAlign: 'center' }}>
            EARNINGS DATES VIA POLYGON · NOT INVESTMENT ADVICE
          </div>
          <div style={{ height: 16 }} />
        </div>

        <div style={{ flexShrink: 0, borderTop: '1px solid hsl(var(--studio-border) / 0.5)', background: 'hsl(var(--studio-background) / 0.72)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
          <TickerStrip />
        </div>
        <BottomNav />
      </div>
    </TickerCtx.Provider>
  )
}

export function Calendar() {
  return (
    <div className="v3-root" style={{ height: 'var(--v3-vh, 100dvh)', display: 'flex', flexDirection: 'column' }}>
      <StockDetailProvider>
        <CalendarBody />
      </StockDetailProvider>
    </div>
  )
}

export default Calendar
